import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { useAiEntitlement } from "@/hooks/useAiEntitlement";
import { Plus, X, Trash2, Copy, Sparkles, ArrowRight, Link2, Handshake, Lock, ShieldCheck, ShieldAlert, Clock, FileEdit, Rocket } from "lucide-react";
import { toast } from "sonner";
import UpsellModal from "@/components/UpsellModal";

const pad = (n) => String(n).padStart(2, "0");

const toLocalInput = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 16);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

const toIso = (localVal) => {
  if (!localVal) return null;
  try {
    const d = new Date(localVal);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
};

const EMPTY = {
  name: "",
  description: "",
  campaign_type: "online",
  redemption_method: "COUPON_CODE",
  mode: "dynamic",
  discount_type: "percent",
  discount_value: 15,
  universal_code: "",
  max_coupons: 100,
  per_customer_limit: 1,
  expires_at: "",
  redirect_url: "",
  landing_page_url: "",
  business_name: "",
  store_location: "",
  store_address: "",
  terms_conditions: "",
  featured: false,
  flash_sale: false,
  cashback_percent: 0,
  storefront_id: "",
  provinces: [],
};

export default function MerchantCampaigns() {
  const { aiEnabled } = useAiEntitlement();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState(null);
  const [codes, setCodes] = useState([]);
  const [nudge, setNudge] = useState(null); // { campaign, data } | null
  const [nudgeBusy, setNudgeBusy] = useState(false);
  const [upsell, setUpsell] = useState(null); // backend 402 detail
  const [billing, setBilling] = useState(null);

  const [limits, setLimits] = useState(null);
  // COUPON-ELIGIBILITY-1 Phase A · storefront verification state, keyed by
  // storefront_id. Lazily hydrated when the modal opens.
  const [verifications, setVerifications] = useState({});
  const [storefronts, setStorefronts] = useState([]);
  const [defaultStorefrontId, setDefaultStorefrontId] = useState(null);
  // Draft-edit mode. When set, submit PUTs to this campaign id instead of
  // POSTing a new draft.
  const [editingDraftId, setEditingDraftId] = useState(null);

  const hydrateVerifications = async () => {
    try {
      const { data } = await api.get("/v1/merchant/storefronts");
      const list = data?.storefronts || [];
      setStorefronts(list);
      if (list.length && !defaultStorefrontId) {
        setDefaultStorefrontId(list[0].id);
      }
      const entries = await Promise.all(list.map(async (sf) => {
        try {
          const { data: v } = await api.get(
            `/v1/merchant/storefronts/${sf.id}/checkout-verification`
          );
          return [sf.id, v];
        } catch { return [sf.id, { status: "NOT_CHECKED" }]; }
      }));
      setVerifications(Object.fromEntries(entries));
    } catch { /* ignore — modal still opens, banner just won't show */ }
  };

  useEffect(() => {
    if (modal) hydrateVerifications();
    // MERCHANT-ONBOARDING-REVIEW-1 add-on · If the merchant loaded a
    // starter template inside the Storefront Setup Guide, offer to prefill
    // this new-campaign modal with the matching sample offer. Only once
    // per template — we clear the stash after use.
    if (modal && !editingDraftId) {
      try {
        const raw = localStorage.getItem("cy_sample_offer_prefill");
        if (raw) {
          const t = JSON.parse(raw);
          setForm((f) => ({
            ...f,
            name: f.name || t.name || "",
            description: f.description || t.description || "",
            redemption_method: t.redemption_method || f.redemption_method,
            discount_type: t.discount_type || f.discount_type,
            discount_value: t.discount_value ?? f.discount_value,
          }));
          toast.success("Sample offer loaded — tweak the details and save.");
          localStorage.removeItem("cy_sample_offer_prefill");
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  const openEditDraft = (c) => {
    setEditingDraftId(c.id);
    setForm({
      ...EMPTY,
      ...c,
      // Pydantic requires exact fields; coerce nullables to defaults.
      max_coupons: c.max_coupons ?? 100,
      per_customer_limit: c.per_customer_limit ?? 1,
      cashback_percent: c.cashback_percent ?? 0,
      provinces: c.provinces || [],
      storefront_id: c.storefront_id || "",
      universal_code: c.universal_code || "",
      redirect_url: c.redirect_url || "",
      landing_page_url: c.landing_page_url || "",
      business_name: c.business_name || "",
      store_location: c.store_location || "",
      store_address: c.store_address || "",
      terms_conditions: c.terms_conditions || "",
      expires_at: toLocalInput(c.expires_at),
      description: c.description || "",
    });
    setModal(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const chosenSid = form.storefront_id || defaultStorefrontId;
      const chosenSf = storefronts.find((s) => s.id === chosenSid);
      const inferredProvinces = (form.provinces && form.provinces.length)
        ? form.provinces
        : (chosenSf?.provinces || []);
      const payload = {
        ...form,
        storefront_id: chosenSid || null,
        provinces: inferredProvinces,
        discount_value: Number(form.discount_value),
        max_coupons: form.max_coupons ? Number(form.max_coupons) : null,
        per_customer_limit: Number(form.per_customer_limit),
        expires_at: toIso(form.expires_at),
        redirect_url: form.redirect_url?.trim() || null,
        landing_page_url: form.landing_page_url?.trim() || null,
        universal_code: form.universal_code?.trim() || null,
        business_name: form.business_name?.trim() || null,
        store_location: form.store_location?.trim() || null,
        store_address: form.store_address?.trim() || null,
        terms_conditions: form.terms_conditions?.trim() || null,
      };
      // MERCHANT-ONBOARDING-REVIEW-1 Phase 1 — new manual campaigns start as
      // a DRAFT. When editing an existing draft, PUT preserves the draft
      // status so we can loop back to Review.
      let data;
      if (editingDraftId) {
        const r = await api.put(
          `/merchant/campaigns/${editingDraftId}`, payload,
        );
        data = r.data;
        toast.success("Draft updated");
      } else {
        const r = await api.post("/merchant/campaigns/draft", payload);
        data = r.data;
        toast.success("Draft saved — review before publishing");
      }
      setModal(false);
      setForm(EMPTY);
      const targetId = editingDraftId || data.id;
      setEditingDraftId(null);
      navigate(`/merchant/campaigns/${targetId}/review`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402 && detail && typeof detail === "object") {
        setModal(false);
        setUpsell(detail);
        return;
      }
      // Drafts don't fire the coupon gate at save-time (that fires at
      // publish/Review), but keep the branch for safety.
      if (err.response?.status === 400 && detail && typeof detail === "object"
          && detail.error === "coupon_checkout_not_verified") {
        toast.error(detail.message || "Checkout verification required");
        return;
      }
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to save draft"));
    }
  };

  const askNudge = async (c) => {
    setNudgeBusy(true);
    setNudge({ campaign: c, data: null });
    try {
      const { data } = await api.post("/ai/merchant/copy-nudge", { campaign_id: c.id });
      setNudge({ campaign: c, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI unavailable — try again");
      setNudge(null);
    } finally {
      setNudgeBusy(false);
    }
  };

  const load = async () => {
    const [{ data }, { data: b }, { data: lim }] = await Promise.all([
      api.get("/merchant/campaigns"),
      api.get("/billing/me").catch(() => ({ data: null })),
      api.get("/merchant/campaign-limits").catch(() => ({ data: null })),
    ]);
    setItems(data);
    setBilling(b);
    setLimits(lim);
  };
  useEffect(() => { load(); }, []);

  // Reopen the edit-draft modal when the Review page routes back with a
  // location.state hint (see CampaignReview.js "Back to Edit").
  useEffect(() => {
    const target = location.state?.editDraftId;
    if (!target || !items.length) return;
    const c = items.find((x) => x.id === target);
    if (c) {
      openEditDraft(c);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, items]);

  const openCodes = async (c) => {
    setSelected(c);
    const { data } = await api.get(`/merchant/campaigns/${c.id}/codes`);
    setCodes(data);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete campaign?")) return;
    await api.delete(`/merchant/campaigns/${id}`);
    load();
    if (selected?.id === id) { setSelected(null); setCodes([]); }
  };

  const toggleResellerVisibility = async (c) => {
    const next = !c.available_to_resellers;
    // Optimistic update
    setItems(cur => cur.map(x => x.id === c.id ? { ...x, available_to_resellers: next } : x));
    try {
      await api.patch(`/merchant/campaigns/${c.id}/reseller-visibility`, { available_to_resellers: next });
      toast.success(next
        ? "Now visible to approved resellers"
        : "Hidden from the reseller catalog");
    } catch (e) {
      // Revert on failure
      setItems(cur => cur.map(x => x.id === c.id ? { ...x, available_to_resellers: !next } : x));
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Failed to update"));
    }
  };

  return (
    <div data-testid="merchant-campaigns">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="pf-chip pf-chip-cyan">Promotion engine</div>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Campaigns</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">Create a promotion — Cyannect auto-generates unique coupon codes for every customer who claims it.</p>
        </div>
        <div className="flex items-center gap-2">
          {limits && !limits.unlimited && (
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                limits.can_create ? "bg-slate-50 border-slate-200 text-slate-700" : "bg-rose-50 border-rose-200 text-rose-800"
              }`}
              data-testid="campaign-cap-pill"
              title={limits.can_create ? "Free plan limit" : "Upgrade to launch more"}
            >
              {limits.active_campaigns_count} of {limits.max_active_campaigns} used
              {!limits.can_create && <Lock className="w-3 h-3" />}
            </span>
          )}
          <div className="relative group">
            <button
              onClick={() => (limits && !limits.can_create ? window.location.assign("/merchant/billing") : setModal(true))}
              disabled={limits ? !limits.can_create : false}
              className="pf-btn-primary px-4 py-2 rounded-full font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="new-campaign-btn"
            >
              <Plus className="w-4 h-4" /> New campaign
            </button>
            {limits && !limits.can_create && (
              <div className="absolute right-0 mt-2 w-64 rounded-xl bg-slate-900 text-white text-xs p-3 shadow-xl opacity-0 group-hover:opacity-100 transition pointer-events-none z-10"
                   data-testid="cap-upsell-tooltip">
                You've hit the Free plan's {limits.max_active_campaigns}-campaign cap. Upgrade to Growth to launch unlimited campaigns.
              </div>
            )}
          </div>
        </div>
      </div>

      {limits && !limits.can_create && (
        <div className="pf-card p-4 mb-4 flex items-center justify-between gap-4 bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-100"
             data-testid="cap-upsell-banner">
          <div className="flex items-center gap-3">
            <Lock className="w-5 h-5 text-rose-600 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-slate-900">Free plan campaign limit reached</div>
              <div className="text-xs text-slate-600 mt-0.5">
                You're using all {limits.max_active_campaigns} campaigns. Archive one or upgrade for unlimited launches + lower commission.
              </div>
            </div>
          </div>
          <a href="/merchant/billing" className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 shrink-0"
             data-testid="cap-upsell-cta">
            Upgrade to Growth <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      )}

      {billing?.usage?.campaign_cap != null && (
        <div className="pf-card p-4 mb-4 flex items-center gap-4" data-testid="tier-usage-bar">
          <div className="pf-chip pf-chip-lav">{(billing.tier || "starter").toUpperCase()} plan</div>
          <div className="flex-1">
            <div className="flex items-baseline justify-between mb-1">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Active campaigns</div>
              <div className="text-xs font-mono text-slate-500">
                {billing.usage.active_campaigns} / {billing.usage.campaign_cap}
              </div>
            </div>
            <div className="h-2 rounded-full bg-white/60 overflow-hidden">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-all"
                style={{ width: `${Math.min(100, (billing.usage.active_campaigns / billing.usage.campaign_cap) * 100)}%` }}
              />
            </div>
          </div>
          <a href="/merchant/billing" className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="tier-upgrade-link">
            <Sparkles className="w-3 h-3" /> Upgrade
          </a>
        </div>
      )}

      <div className="pf-card overflow-hidden">
        <div className="flex items-center gap-2 p-3 border-b border-white/60" data-testid="campaigns-filter-tabs">
          {[
            { k: "all", label: "All" },
            { k: "draft", label: "Drafts", badge: items.filter((c) => c.status === "draft").length },
            { k: "pending", label: "Pending review", badge: items.filter((c) => c.status === "pending").length },
            { k: "approved", label: "Approved" },
            { k: "archived", label: "Paused" },
            { k: "rejected", label: "Rejected" },
          ].map((t) => (
            <button key={t.k} onClick={() => setStatusFilter(t.k)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition
                      ${statusFilter === t.k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                    data-testid={`campaigns-filter-${t.k}`}>
              {t.label}
              {t.badge > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">{t.badge}</span>
              )}
            </button>
          ))}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-5 py-3">Campaign</th>
              <th className="px-5 py-3">Mode</th>
              <th className="px-5 py-3">Discount</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Claims</th>
              <th className="px-5 py-3">Redeemed</th>
              <th className="px-5 py-3">Revenue</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items
              .filter((c) => statusFilter === "all" ? true : c.status === statusFilter)
              .map((c) => (
              <tr key={c.id} className="border-t border-white/60" data-testid={`campaign-row-${c.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">
                  {c.name}
                  {c.source === "ai_bulk_draft" && (
                    <span className="ml-2 pf-chip pf-chip-cyan text-[10px]" title="AI-drafted">
                      <Sparkles className="w-2.5 h-2.5" /> AI
                    </span>
                  )}
                </td>
                <td className="px-5 py-3"><span className="pf-chip">{c.mode}</span></td>
                <td className="px-5 py-3">{c.discount_value}{c.discount_type === "percent" ? "%" : ""} {c.discount_type}</td>
                <td className="px-5 py-3"><span className={`pf-chip ${c.status === "approved" ? "pf-chip-mint" : c.status === "rejected" ? "" : c.status === "pending" ? "pf-chip-amber" : c.status === "draft" ? "pf-chip-cyan" : "pf-chip-lav"}`} data-testid={`campaign-status-${c.id}`}>{c.status === "archived" ? "paused" : c.status}</span></td>
                <td className="px-5 py-3 font-mono">{c.claims || 0}</td>
                <td className="px-5 py-3 font-mono">{c.redemptions || 0}</td>
                <td className="px-5 py-3 font-mono">{cad((c.revenue || 0))}</td>
                <td className="px-5 py-3 text-right space-x-1">
                  {c.status === "approved" && (
                    <button onClick={async () => {
                              try {
                                await api.patch(`/merchant/campaigns/${c.id}`, { status: "archived" });
                                toast.success("Offer paused.");
                                load();
                              } catch { toast.error("Pause failed"); }
                            }}
                            className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold"
                            data-testid={`campaign-pause-${c.id}`}>
                      Pause
                    </button>
                  )}
                  {c.status === "archived" && (
                    <button onClick={async () => {
                              try {
                                await api.patch(`/merchant/campaigns/${c.id}`, { status: "approved" });
                                toast.success("Offer resumed and live.");
                                load();
                              } catch { toast.error("Resume failed"); }
                            }}
                            className="pf-btn-primary px-3 py-1 rounded-full text-xs font-semibold"
                            data-testid={`campaign-resume-${c.id}`}>
                      Resume
                    </button>
                  )}
                  {c.status === "pending" && (
                    <span className="text-xs text-amber-700 px-2" title="Waiting on Cyannect admin review"
                           data-testid={`campaign-pending-${c.id}`}>Awaiting review</span>
                  )}
                  {c.status === "draft" && (
                    <>
                      <button type="button"
                              onClick={() => openEditDraft(c)}
                              className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                              data-testid={`campaign-edit-draft-${c.id}`}>
                        <FileEdit className="w-3 h-3" /> Edit
                      </button>
                      <Link to={`/merchant/campaigns/${c.id}/review`}
                            className="pf-btn-primary px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1"
                            data-testid={`campaign-review-${c.id}`}>
                        <Rocket className="w-3 h-3" /> Review &amp; Publish
                      </Link>
                    </>
                  )}
                  {(c.campaign_type === "online" || c.campaign_type === "hybrid") && (
                    <button
                      onClick={() => {
                        const url = `${window.location.origin}/go/campaign/${c.id}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Tracker link copied — paste into emails, socials, or print QR");
                      }}
                      className="text-xs font-semibold px-2.5 py-1 rounded-full pf-btn-glass inline-flex items-center gap-1"
                      data-testid={`copy-tracker-${c.id}`}
                      title="Copy tracked outbound URL"
                    >
                      <Link2 className="w-3 h-3 text-cyan-600" /> Link
                    </button>
                  )}
                  <button
                    onClick={() => toggleResellerVisibility(c)}
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition ${
                      c.available_to_resellers
                        ? "bg-emerald-500 hover:bg-emerald-400 text-white"
                        : "pf-btn-glass"
                    }`}
                    data-testid={`reseller-toggle-${c.id}`}
                    title={c.available_to_resellers
                      ? "Live in the reseller catalog — click to hide"
                      : "Publish to approved resellers"}
                  >
                    <Handshake className={`w-3 h-3 ${c.available_to_resellers ? "" : "text-emerald-600"}`} />
                    {c.available_to_resellers ? "Live" : "Resellers"}
                  </button>
                  {aiEnabled ? (
                  <button onClick={() => askNudge(c)} className="text-xs font-semibold px-2.5 py-1 rounded-full pf-btn-glass inline-flex items-center gap-1" data-testid={`ai-nudge-${c.id}`} title="AI copy suggestions">
                    <Sparkles className="w-3 h-3 text-cyan-600" /> AI
                  </button>
                  ) : (
                  <Link to="/merchant/billing" className="text-xs font-semibold px-2.5 py-1 rounded-full pf-btn-glass inline-flex items-center gap-1 text-slate-500" data-testid={`ai-nudge-locked-${c.id}`} title="AI copy suggestions are a Growth plan feature. Upgrade to Growth or change your plan.">
                    <Lock className="w-3 h-3" /> AI · Upgrade
                  </Link>
                  )}
                  <button onClick={() => openCodes(c)} className="text-xs font-semibold px-3 py-1 rounded-full pf-btn-glass" data-testid={`view-codes-${c.id}`}>View codes</button>
                  <button onClick={() => remove(c.id)} className="p-1.5 rounded hover:bg-white/70 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="px-5 py-12 text-center text-slate-500">No campaigns yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="mt-6 pf-card p-6" data-testid="codes-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Codes for</div>
              <div className="font-display text-2xl text-slate-900">{selected.name}</div>
            </div>
            <button onClick={() => { setSelected(null); setCodes([]); }} className="text-slate-500"><X className="w-5 h-5" /></button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/40 text-left text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-3 py-2">Code</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Issued</th></tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} className="border-t border-white/60">
                    <td className="px-3 py-2 font-mono font-semibold flex items-center gap-2">{c.code || "(auto-apply)"}
                      {c.code && <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Copied"); }}><Copy className="w-3 h-3 text-slate-500" /></button>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono">{c.customer_id?.slice(0, 16)}…</td>
                    <td className="px-3 py-2"><span className={`pf-chip ${c.status === "redeemed" ? "pf-chip-mint" : c.status === "validated" ? "pf-chip-cyan" : ""}`}>{c.status}</span></td>
                    <td className="px-3 py-2 text-xs text-slate-500">{new Date(c.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {codes.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-500">No codes issued yet — share the campaign link with shoppers.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => { setModal(false); setEditingDraftId(null); setForm(EMPTY); }}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="glass-strong rounded-3xl w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" data-testid="campaign-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl text-slate-900">{editingDraftId ? "Edit draft" : "New campaign"}</h3>
              <button type="button" onClick={() => { setModal(false); setEditingDraftId(null); setForm(EMPTY); }}><X className="w-5 h-5" /></button>
            </div>

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-2">Campaign name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="pf-input w-full px-3 py-2 mt-1 text-sm" data-testid="cf-name" />

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="pf-input w-full px-3 py-2 mt-1 text-sm" />

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Campaign type</label>
            <div className="grid grid-cols-1 gap-2 mt-1">
              {[["online", "Online", "Website / e-commerce"]].map(([v, label, sub]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, campaign_type: v })} className={`p-3 rounded-xl text-left transition ${form.campaign_type === v ? "pf-btn-primary" : "pf-btn-glass"}`} data-testid={`cf-ctype-${v}`}>
                  <div className="font-semibold text-sm">{label}</div>
                  <div className="text-[10px] opacity-80 mt-0.5">{sub}</div>
                </button>
              ))}
            </div>

            {(form.campaign_type === "offline" || form.campaign_type === "hybrid") && (
              <div className="pf-card p-4 mt-3 space-y-2 bg-emerald-50/40">
                <div className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Offline details</div>
                <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="pf-input w-full px-3 py-2 text-sm" placeholder="Business name (as shown to shopper)" data-testid="cf-business-name" />
                <input value={form.store_location} onChange={(e) => setForm({ ...form, store_location: e.target.value })} className="pf-input w-full px-3 py-2 text-sm" placeholder="Store location label (e.g. Downtown Toronto)" data-testid="cf-store-location" />
                <input value={form.store_address} onChange={(e) => setForm({ ...form, store_address: e.target.value })} className="pf-input w-full px-3 py-2 text-sm" placeholder="Full street address" data-testid="cf-store-address" />
                <textarea value={form.terms_conditions} onChange={(e) => setForm({ ...form, terms_conditions: e.target.value })} rows="2" className="pf-input w-full px-3 py-2 text-sm" placeholder="Terms & conditions (fine print shown to shopper)" data-testid="cf-terms" />
              </div>
            )}

            {/* Storefront picker — hidden when merchant has only one storefront */}
            {storefronts.length > 1 && (
              <>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Storefront *</label>
                <select value={form.storefront_id || defaultStorefrontId || ""}
                        onChange={(e) => setForm({ ...form, storefront_id: e.target.value, provinces: [] })}
                        className="pf-input w-full px-3 py-2 mt-1 text-sm"
                        data-testid="cf-storefront">
                  {storefronts.map((sf) => (
                    <option key={sf.id} value={sf.id}>{sf.name}</option>
                  ))}
                </select>
              </>
            )}

            {/* Province coverage — inherited from storefront by default */}
            {(() => {
              const sfId = form.storefront_id || defaultStorefrontId;
              const sf = storefronts.find((s) => s.id === sfId);
              const sfProv = sf?.provinces || [];
              if (!sfProv.length) return null;
              const current = form.provinces && form.provinces.length ? form.provinces : sfProv;
              const toggle = (p) => {
                const next = current.includes(p)
                  ? current.filter((x) => x !== p)
                  : [...current, p];
                setForm({ ...form, provinces: next });
              };
              return (
                <>
                  <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Province coverage</label>
                  <div className="flex flex-wrap gap-1.5 mt-1" data-testid="cf-provinces">
                    {sfProv.map((p) => (
                      <button key={p} type="button"
                              onClick={() => toggle(p)}
                              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${current.includes(p) ? "pf-btn-primary" : "pf-btn-glass"}`}
                              data-testid={`cf-province-${p}`}>
                        {p}
                      </button>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Defaults to every province enabled on this storefront. Tap to narrow.
                  </div>
                </>
              );
            })()}

            {/* CF-1 dual redemption — merchant picks how the shopper redeems */}
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">How should shoppers redeem this offer?</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
              {[
                ["COUPON_CODE", "Coupon Code", "Fastest setup", "Cyannect generates a unique coupon that shoppers copy and enter at your checkout. Simpler integration — merchant checkout validates the code with Cyannect."],
                ["AUTO_DISCOUNT", "Automatic Discount", "Best shopper experience", "No coupon code shown. Cyannect passes a secure referral to your site and your checkout applies the discount automatically. Requires one-time Cyannect checkout integration."],
              ].map(([v, label, tag, desc]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setForm({ ...form, redemption_method: v })}
                  className={`p-3 rounded-xl text-left transition ${form.redemption_method === v ? "pf-btn-primary" : "pf-btn-glass"}`}
                  data-testid={`cf-rmethod-${v.toLowerCase()}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm">{label}</div>
                    <span className="text-[10px] uppercase tracking-widest opacity-75">{tag}</span>
                  </div>
                  <div className="text-[11px] opacity-80 mt-1 leading-snug">{desc}</div>
                </button>
              ))}
            </div>

            {form.redemption_method === "COUPON_CODE" && (() => {
              const sfId = form.storefront_id || defaultStorefrontId;
              const v = sfId ? verifications[sfId] : null;
              const status = v?.status || "NOT_CHECKED";
              if (status === "VERIFIED") {
                return (
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2 text-xs text-emerald-800"
                       data-testid="cf-gate-verified">
                    <ShieldCheck className="w-3.5 h-3.5 mt-0.5" />
                    <div>
                      <div className="font-semibold">Checkout verified</div>
                      <div>You can publish Coupon Code offers on this storefront.</div>
                    </div>
                  </div>
                );
              }
              if (status === "PENDING_REVIEW") {
                return (
                  <div className="mt-3 flex items-start gap-2 rounded-xl bg-sky-50 ring-1 ring-sky-200 px-3 py-2 text-xs text-sky-800"
                       data-testid="cf-gate-pending">
                    <Clock className="w-3.5 h-3.5 mt-0.5" />
                    <div>
                      <div className="font-semibold">Checkout submitted for review</div>
                      <div>Coupon Code offers will unlock once approved. You'll still be able to create Automatic Discount offers.</div>
                    </div>
                  </div>
                );
              }
              if (status === "NOT_SUPPORTED") {
                return (
                  <div className="mt-3 rounded-xl bg-rose-50 ring-1 ring-rose-200 px-3 py-2 text-xs text-rose-800"
                       data-testid="cf-gate-not-supported">
                    <div className="flex items-start gap-2">
                      <ShieldAlert className="w-3.5 h-3.5 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold">Coupon Code isn't currently supported by this checkout</div>
                        <div>{v?.verification_reason || "We couldn't verify a shopper-facing field where customers can enter a coupon or promotional code."}</div>
                      </div>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <button type="button"
                              onClick={() => setForm({ ...form, redemption_method: "AUTO_DISCOUNT" })}
                              className="pf-btn-primary px-3 py-1 rounded-full text-[11px] font-semibold"
                              data-testid="cf-gate-switch-auto">
                        Use Automatic Discount
                      </button>
                      <Link to="/merchant/checkout-verification"
                            className="pf-btn-glass px-3 py-1 rounded-full text-[11px] font-semibold">
                        Resubmit checkout
                      </Link>
                    </div>
                  </div>
                );
              }
              // NOT_CHECKED (or missing) → prompt to verify.
              return (
                <div className="mt-3 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-xs text-amber-800"
                     data-testid="cf-gate-not-checked">
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="w-3.5 h-3.5 mt-0.5" />
                    <div className="flex-1">
                      <div className="font-semibold">Checkout Verification Required</div>
                      <div>To use Coupon Code redemption, shoppers must have a visible place to enter or paste a promo or coupon code during checkout.</div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <Link to="/merchant/checkout-verification"
                          className="pf-btn-primary px-3 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1"
                          data-testid="cf-gate-verify-cta">
                      <ShieldCheck className="w-3.5 h-3.5" /> Verify Checkout
                    </Link>
                  </div>
                </div>
              );
            })()}


            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Coupon mode</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[["dynamic", "Dynamic", "Unique code per customer"], ["universal", "Universal", "One shared code"], ["auto_apply", "Auto-apply", "No code shown"]].map(([v, label, sub]) => (
                <button key={v} type="button" onClick={() => setForm({ ...form, mode: v })} className={`p-3 rounded-xl text-left transition ${form.mode === v ? "pf-btn-primary" : "pf-btn-glass"}`} data-testid={`cf-mode-${v}`}>
                  <div className="font-semibold text-sm">{label}</div>
                  <div className="text-[10px] opacity-80 mt-0.5">{sub}</div>
                </button>
              ))}
            </div>

            {form.mode === "universal" && (
              <>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Universal code *</label>
                <input required value={form.universal_code} onChange={(e) => setForm({ ...form, universal_code: e.target.value.toUpperCase() })} className="pf-input w-full px-3 py-2 mt-1 text-sm font-mono" placeholder="SAVE15" />
              </>
            )}

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Discount type</label>
                <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="pf-input w-full px-3 py-2 mt-1 text-sm">
                  <option value="percent">Percent %</option>
                  <option value="fixed">Fixed $</option>
                  <option value="shipping">Free shipping</option>
                  <option value="bogo">BOGO</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Value</label>
                <input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="pf-input w-full px-3 py-2 mt-1 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Max coupons</label>
                <input type="number" value={form.max_coupons} onChange={(e) => setForm({ ...form, max_coupons: e.target.value })} className="pf-input w-full px-3 py-2 mt-1 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Per-customer limit</label>
                <input type="number" value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: e.target.value })} className="pf-input w-full px-3 py-2 mt-1 text-sm" />
              </div>
            </div>

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Redirect URL</label>
            <input value={form.redirect_url} onChange={(e) => setForm({ ...form, redirect_url: e.target.value })} className="pf-input w-full px-3 py-2 mt-1 text-sm" placeholder="https://yourshop.com/checkout" data-testid="cf-redirect-url" />

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Expires</label>
            <input type="datetime-local" value={form.expires_at || ""}
                   onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                   className="pf-input w-full px-3 py-2 mt-1 text-sm"
                   data-testid="cf-expires-at" />
            <div className="text-[11px] text-slate-500 mt-1">Leave blank for an evergreen offer.</div>

            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mt-3">Cashback for shoppers (% of revenue)</label>
            <input type="number" step="0.1" min="0" max="100" value={form.cashback_percent} onChange={(e) => setForm({ ...form, cashback_percent: Number(e.target.value) })} className="pf-input w-full px-3 py-2 mt-1 text-sm" placeholder="0" data-testid="cf-cashback" />
            <div className="text-[11px] text-slate-500 mt-1">Credits the shopper's Cyannect wallet on every successful redemption. Set 0 to disable.</div>

            <div className="flex gap-4 mt-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.flash_sale} onChange={(e) => setForm({ ...form, flash_sale: e.target.checked })} /> Flash sale</label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => { setModal(false); setEditingDraftId(null); setForm(EMPTY); }} className="pf-btn-glass px-4 py-2 rounded-full text-sm">Cancel</button>
              <button type="submit" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold" data-testid="cf-submit">{editingDraftId ? "Save & Review" : "Save Draft & Review"}</button>
            </div>
          </form>
        </div>
      )}

      {nudge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4" onClick={() => setNudge(null)} data-testid="ai-nudge-modal">
          <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-3xl w-full max-w-2xl p-8 relative">
            <button onClick={() => setNudge(null)} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/70 text-slate-500"><X className="w-4 h-4" /></button>
            <div className="pf-chip pf-chip-lav w-fit"><Sparkles className="w-3 h-3" /> AI copywriter</div>
            <h3 className="font-display text-2xl mt-3 text-slate-900">Copy nudges for <span className="pf-gradient-text-2">{nudge.campaign.name}</span></h3>
            {!nudge.data ? (
              <div className="mt-6 text-sm text-slate-500 flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded-full border-2 border-cyan-500 border-t-transparent animate-spin" />
                Generating suggestions with Claude…
              </div>
            ) : (
              <div className="space-y-5 mt-5" data-testid="ai-nudge-content">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Name variants</div>
                  <div className="space-y-2 mt-2">
                    {(nudge.data.names || []).map((n, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 border border-white/60 rounded-xl px-3 py-2 bg-white/50 text-sm">
                        <span className="flex-1 truncate">{n}</span>
                        <button onClick={() => { navigator.clipboard.writeText(n); toast.success("Copied"); }} className="pf-btn-glass px-2 py-1 rounded-full text-[11px] font-semibold inline-flex items-center gap-1" data-testid={`ai-nudge-copy-name-${i}`}>
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description variants</div>
                  <div className="space-y-2 mt-2">
                    {(nudge.data.descriptions || []).map((d, i) => (
                      <div key={i} className="flex items-start justify-between gap-3 border border-white/60 rounded-xl px-3 py-2 bg-white/50 text-sm">
                        <span className="flex-1">{d}</span>
                        <button onClick={() => { navigator.clipboard.writeText(d); toast.success("Copied"); }} className="pf-btn-glass px-2 py-1 rounded-full text-[11px] font-semibold shrink-0 inline-flex items-center gap-1" data-testid={`ai-nudge-copy-desc-${i}`}>
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                {nudge.data.best_time_to_launch && (
                  <div className="pf-card p-4 bg-gradient-to-br from-cyan-50 to-emerald-50 border-cyan-200">
                    <div className="text-xs uppercase tracking-widest text-cyan-700 font-semibold flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Best time to launch</div>
                    <div className="text-sm text-slate-900 mt-1 font-medium">{nudge.data.best_time_to_launch}</div>
                    {nudge.data.reasoning && <div className="text-xs text-slate-600 mt-2 italic">{nudge.data.reasoning}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <UpsellModal open={!!upsell} detail={upsell} onClose={() => setUpsell(null)} />
    </div>
  );
}
